import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Tag, Printer, Download, Loader2, FileText, X } from 'lucide-react';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';

/**
 * Eleganter Etiketten-Vorschau-Dialog im Homeyoursoul-Look.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - mode: 'single' | 'all'
 *  - product: { id, name } (für mode='single')
 *  - title: optionaler Header-Text (für mode='all')
 *
 * Lädt die PDF als Blob, zeigt sie in einem <iframe> und bietet drei Aktionen:
 *  1. Drucken (Browser-Druckdialog)
 *  2. Herunterladen (PDF-Datei speichern)
 *  3. Layout-Umschaltung "Einzeletikett ↔ A4-Bogen mit X Kopien"
 */
const LabelPreviewDialog = ({ open, onClose, mode = 'single', product = null, title = null }) => {
  const [layout, setLayout] = useState('single'); // 'single' | 'a4_sheet'
  const [copies, setCopies] = useState(12);
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  const isBulk = mode === 'all';

  const buildUrl = () => {
    if (isBulk) {
      return '/products/labels/all';
    }
    if (!product) return null;
    return `/products/${product.id}/label-pdf`;
  };

  const buildParams = () => {
    if (isBulk) return {};
    return { layout, copies };
  };

  const loadPdf = async () => {
    const url = buildUrl();
    if (!url) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(url, {
        params: buildParams(),
        responseType: 'blob',
      });
      const newUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      setBlobUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return newUrl;
      });
    } catch (error) {
      console.error('Label preview error:', error);
      toast.error('Etikett-Vorschau konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  // (Re)load when dialog opens or layout/copies change
  useEffect(() => {
    if (!open) return;
    loadPdf();
  }, [open, mode, product?.id, layout, copies]);

  // Reset state and cleanup when closing
  useEffect(() => {
    if (open) return;
    setBlobUrl((prev) => {
      if (prev) window.URL.revokeObjectURL(prev);
      return null;
    });
    setLayout('single');
    setCopies(12);
  }, [open]);

  const handlePrint = () => {
    if (!blobUrl) return;
    const iframe = document.getElementById('label-preview-iframe');
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        return;
      } catch (e) {
        // fallback to opening in new window
      }
    }
    const w = window.open(blobUrl, '_blank');
    if (w) {
      setTimeout(() => {
        try {
          w.focus();
          w.print();
        } catch (e) {
          /* user can still print from new tab */
        }
      }, 500);
    }
  };

  const handleOpenInNewTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl, '_blank', 'noopener');
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const safeName = isBulk
      ? 'etiketten-alle-artikel'
      : `etikett-${(product?.name || 'artikel').toLowerCase().replace(/[^a-z0-9äöüß]+/gi, '-').slice(0, 50)}`;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `${safeName}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const headerTitle = title || (isBulk
    ? 'Etiketten – alle Artikel'
    : `Etikett – ${product?.name || 'Artikel'}`);

  const layoutHint = isBulk
    ? 'Ein Etikett pro Artikel auf einem A4-Bogen (5 × 9 = 45 pro Seite). 40 × 30 mm.'
    : (layout === 'single'
        ? 'Eine einzelne Etikettenseite (40 × 30 mm).'
        : `A4-Bogen mit ${copies} Kopien dieses Artikels (40 × 30 mm, 5 × 9 = 45 / Seite).`);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col p-0 rounded-none"
        style={{ backgroundColor: '#FAF5EE' }}
        data-testid="label-preview-dialog"
      >
        {/* Header – warme Markenfarbe */}
        <DialogHeader className="p-0">
          <div className="px-4 py-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
            <div className="w-7 h-7 rounded flex items-center justify-center bg-[#FAF5EE]">
              <Tag className="h-4 w-4 text-[#A07F2E]" />
            </div>
            <DialogTitle className="text-white text-base">
              {headerTitle}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              className="ml-auto text-white/90 hover:text-white"
              aria-label="Schließen"
              data-testid="label-preview-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 grid md:grid-cols-[260px_1fr] gap-0 overflow-hidden">
          {/* Sidebar – Layout-Optionen */}
          <div className="px-4 py-3 border-r border-[#E8DCC8] bg-[#FAF5EE] flex flex-col gap-3 text-[#5D4E3C] text-sm">
            <div>
              <p className="text-xs text-[#8B7355] mb-1">Format</p>
              <div className="flex items-center gap-1 text-xs bg-white border border-[#E8DCC8] px-2 py-1.5">
                <FileText className="h-3.5 w-3.5 text-[#C4A084]" />
                <span className="font-medium">40 × 30 mm</span>
              </div>
            </div>

            {!isBulk && (
              <div className="space-y-2">
                <p className="text-xs text-[#8B7355]">Layout</p>
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    onClick={() => setLayout('single')}
                    className={`text-xs px-2 py-2 border ${layout === 'single' ? 'bg-[#C4A084] text-white border-[#C4A084]' : 'bg-white text-[#5D4E3C] border-[#E8DCC8] hover:bg-[#F5EDE4]'}`}
                    data-testid="label-layout-single"
                  >
                    Einzel
                  </button>
                  <button
                    type="button"
                    onClick={() => setLayout('a4_sheet')}
                    className={`text-xs px-2 py-2 border ${layout === 'a4_sheet' ? 'bg-[#C4A084] text-white border-[#C4A084]' : 'bg-white text-[#5D4E3C] border-[#E8DCC8] hover:bg-[#F5EDE4]'}`}
                    data-testid="label-layout-sheet"
                  >
                    A4-Bogen
                  </button>
                </div>

                {layout === 'a4_sheet' && (
                  <div>
                    <Label className="text-xs text-[#8B7355]">Kopien</Label>
                    <Input
                      type="number"
                      min={1}
                      max={200}
                      value={copies}
                      onChange={(e) => setCopies(Math.max(1, Math.min(200, parseInt(e.target.value || '1', 10))))}
                      className="mt-1 h-8 rounded-none border-[#D4C4B0] text-sm bg-white"
                      data-testid="label-copies-input"
                    />
                    <p className="text-[10px] text-[#9C8C7A] mt-1">max. 45 pro A4-Seite</p>
                  </div>
                )}
              </div>
            )}

            <div className="text-[11px] text-[#9C8C7A] leading-snug border-t border-[#E8DCC8] pt-3">
              {layoutHint}
            </div>

            {/* Aktionen */}
            <div className="mt-auto pt-3 border-t border-[#E8DCC8] flex flex-col gap-2">
              <Button
                type="button"
                onClick={handlePrint}
                disabled={!blobUrl || loading}
                className="w-full rounded-none bg-[#C4A084] hover:bg-[#B89160] text-white border-0 font-medium flex items-center justify-center gap-1.5"
                data-testid="label-preview-print"
              >
                <Printer className="h-4 w-4" />
                Drucken
              </Button>
              <Button
                type="button"
                onClick={handleDownload}
                disabled={!blobUrl || loading}
                variant="outline"
                className="w-full rounded-none bg-white hover:bg-[#F5EDE4] border-[#C4A084] text-[#5D4E3C] flex items-center justify-center gap-1.5"
                data-testid="label-preview-download"
              >
                <Download className="h-4 w-4" />
                Herunterladen
              </Button>
              <button
                type="button"
                onClick={handleOpenInNewTab}
                disabled={!blobUrl || loading}
                className="text-[10px] text-[#8B7355] hover:text-[#5D4E3C] underline disabled:opacity-50"
                data-testid="label-preview-newtab"
              >
                Im neuen Tab öffnen
              </button>
            </div>
          </div>

          {/* PDF-Vorschau */}
          <div className="relative bg-[#EFE5D7] min-h-[420px] md:min-h-0">
            {loading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[#5D4E3C] gap-2 bg-[#F5EDE4]/80 z-10">
                <Loader2 className="h-6 w-6 animate-spin text-[#C4A084]" />
                <span className="text-xs">Vorschau wird vorbereitet…</span>
              </div>
            )}
            {blobUrl ? (
              <iframe
                id="label-preview-iframe"
                title="Etikett-Vorschau"
                src={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
                className="w-full h-full min-h-[420px]"
                style={{ border: 0 }}
                data-testid="label-preview-iframe"
              />
            ) : !loading ? (
              <div className="absolute inset-0 flex items-center justify-center text-[#9C8C7A] text-sm">
                Keine Vorschau verfügbar
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LabelPreviewDialog;
